import { useLocation } from 'react-router-dom';
import { CaretDown, CaretRight, Icon } from '@phosphor-icons/react';
import { NavLink } from './NavLink';

interface NavItem {
  path: string;
  icon: Icon;
  label: string;
  badge?: string;
  permission: string;
}

interface NavCategory {
  id: string;
  icon: Icon;
  label: string;
  permission: string;
  items: NavItem[];
}

interface Alerts {
  unreadMessages: number;
  urgentMessages: number;
  activeIssues: number;
  urgentIssues: number;
}

interface CategoryNavProps {
  category: NavCategory;
  mobile?: boolean;
  isExpanded: boolean;
  alerts: Alerts;
  onToggle: (categoryId: string) => void;
  onMobileClose?: () => void;
  onFetchAlerts?: () => void;
}

export function CategoryNav({ 
  category, 
  mobile = false, 
  isExpanded, 
  alerts,
  onToggle,
  onMobileClose,
  onFetchAlerts
}: CategoryNavProps) {
  const location = useLocation();
  const IconComponent = category.icon;
  const hasActiveChild = category.items.some(item => location.pathname === item.path);
  
  // Calculate total badges in category
  const categoryBadgeCount = category.items.reduce((total, item) => {
    if (item.badge === 'messages') return total + alerts.unreadMessages;
    if (item.badge === 'issues') return total + alerts.activeIssues;
    return total;
  }, 0);

  return (
    <div className="mb-1">
      <button
        onClick={() => onToggle(category.id)}
        className={`nav-item w-full justify-between ${hasActiveChild ? 'bg-slate-100 text-slate-900' : ''}`}
        data-testid={`nav-category-${category.id}`}
      >
        <span className="flex items-center gap-3">
          <span className="relative">
            <IconComponent size={20} weight={hasActiveChild ? 'fill' : 'regular'} />
            {categoryBadgeCount > 0 && !isExpanded && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
            )}
          </span>
          <span className="font-medium">{category.label}</span>
        </span>
        {isExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
      </button>
      
      {isExpanded && (
        <div className="mt-1 space-y-0.5">
          {category.items.map((item) => (
            <NavLink 
              key={item.path} 
              item={item} 
              mobile={mobile} 
              nested 
              alerts={alerts}
              onMobileClose={onMobileClose}
              onFetchAlerts={onFetchAlerts}
            />
          ))}
        </div>
      )}
    </div>
  );
}
